import Template from "../../components/Template";
import React, { useMemo, useState } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import * as XLSX from "xlsx-js-style";
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb } from "../../utils/masks";

const TOTAIS_ZERADOS = {
    valorBruto: 0,
    valorLiquido: 0,
    diferenca: 0,
    totalRetencoes: 0,
    totalIss: 0,
    totalInss: 0,
    totalIr: 0,
    quantidadeComRetencao: 0,
    quantidadeMaterial: 0,
    quantidadeServico: 0,
};

const RelatorioDeducaoImpostos = () => {
    const [records, setRecords] = useState([]);
    const [cobertura, setCobertura] = useState({ semDadosImposto: 0, totalNotasServicoPeriodo: 0 });
    const [filters, setFilters] = useState({
        dataInicio: "",
        dataFim: "",
        tipo: "servico",
        criterio: "imposto",
    });
    // Obra e fornecedor refinam o resultado já carregado. Não usamos os endpoints
    // de listagem porque eles devolvem apenas obras/fornecedores dos clientes
    // vinculados ao usuário, enquanto o relatório também traz notas sem obra —
    // o select ficaria sem as opções que aparecem na própria tabela.
    const [refino, setRefino] = useState({ fornecedor: "", obra: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [message, setMessage] = useState("");
    const [showAlertDialog, setShowAlertDialog] = useState(false);

    const opcoesFornecedor = useMemo(
        () => Array.from(new Set(records.map((r) => r.fornecedor).filter(Boolean))).sort(),
        [records]
    );
    const opcoesObra = useMemo(
        () => Array.from(new Set(records.map((r) => r.obra || "").filter(Boolean))).sort(),
        [records]
    );

    const recordsExibidos = useMemo(
        () => records.filter((r) => {
            if (refino.fornecedor && r.fornecedor !== refino.fornecedor) return false;
            if (refino.obra && (r.obra || "") !== refino.obra) return false;
            return true;
        }),
        [records, refino]
    );

    const totais = useMemo(
        () => recordsExibidos.reduce((acc, r) => ({
            valorBruto: acc.valorBruto + (Number(r.valorBruto) || 0),
            valorLiquido: acc.valorLiquido + (Number(r.valorLiquido) || 0),
            diferenca: acc.diferenca + (Number(r.diferenca) || 0),
            totalRetencoes: acc.totalRetencoes + (Number(r.valorTotalRetencoes) || 0),
            totalIss: acc.totalIss + (Number(r.valorIss) || 0),
            totalInss: acc.totalInss + (Number(r.valorInss) || 0),
            totalIr: acc.totalIr + (Number(r.valorIr) || 0),
            quantidadeComRetencao: acc.quantidadeComRetencao + ((Number(r.valorTotalRetencoes) || 0) > 0 ? 1 : 0),
            quantidadeMaterial: acc.quantidadeMaterial + (r.tipo === "Material" ? 1 : 0),
            quantidadeServico: acc.quantidadeServico + (r.tipo === "Serviço" ? 1 : 0),
        }), TOTAIS_ZERADOS),
        [recordsExibidos]
    );

    const fetchRelatorio = async () => {
        if (filters.dataInicio && filters.dataFim && filters.dataFim < filters.dataInicio) {
            setMessage("A data final não pode ser anterior à data inicial.");
            setShowAlertDialog(true);
            return;
        }

        setIsLoading(true);
        setHasSearched(true);
        try {
            const params = new URLSearchParams();
            if (filters.dataInicio) params.append("dataInicio", filters.dataInicio);
            if (filters.dataFim) params.append("dataFim", filters.dataFim);
            if (filters.tipo) params.append("tipo", filters.tipo);
            if (filters.criterio) params.append("criterio", filters.criterio);

            const response = await api.get(`/relatorios/deducao-impostos?${params.toString()}`);
            setRecords(response.data.records || []);
            setCobertura({
                semDadosImposto: response.data.totais?.semDadosImposto || 0,
                totalNotasServicoPeriodo: response.data.totais?.totalNotasServicoPeriodo || 0,
            });
            setRefino({ fornecedor: "", obra: "" });
        } catch (error) {
            console.error("Erro ao buscar relatório de dedução de impostos:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar dados do relatório.");
            setShowAlertDialog(true);
            setRecords([]);
            setCobertura({ semDadosImposto: 0, totalNotasServicoPeriodo: 0 });
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => fetchRelatorio();

    const clearFilters = () => {
        setFilters({ dataInicio: "", dataFim: "", tipo: "servico", criterio: "imposto" });
        setRefino({ fornecedor: "", obra: "" });
        setRecords([]);
        setCobertura({ semDadosImposto: 0, totalNotasServicoPeriodo: 0 });
        setHasSearched(false);
    };

    const periodoDescricao = () => {
        if (filters.dataInicio && filters.dataFim) {
            return `${formatDateWithoutHour(filters.dataInicio)} a ${formatDateWithoutHour(filters.dataFim)}`;
        }
        if (filters.dataInicio) return `A partir de ${formatDateWithoutHour(filters.dataInicio)}`;
        if (filters.dataFim) return `Até ${formatDateWithoutHour(filters.dataFim)}`;
        return "Todo o período";
    };

    const exportarExcel = () => {
        if (!recordsExibidos || recordsExibidos.length === 0) {
            setMessage("Não há dados para exportar.");
            setShowAlertDialog(true);
            return;
        }

        const borderThin = { style: "thin", color: { rgb: "B0B0B0" } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

        const tituloStyle = {
            font: { bold: true, sz: 12, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "D9E7F5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borders,
        };
        const subtituloStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "left", vertical: "center" },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borders,
        };
        const cellStyle = {
            font: { sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "EAF1F8" } },
            alignment: { vertical: "center", horizontal: "left" },
            border: borders,
        };
        const cellCurrencyStyle = {
            ...cellStyle,
            alignment: { vertical: "center", horizontal: "right" },
            numFmt: "R$ #,##0.00",
        };
        const totalStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "FCE4D6" } },
            alignment: { vertical: "center", horizontal: "right" },
            border: borders,
            numFmt: "R$ #,##0.00",
        };
        const totalLabelStyle = {
            ...totalStyle,
            numFmt: "General",
            alignment: { vertical: "center", horizontal: "right" },
        };

        const cabecalhos = [
            "TIPO",
            "DATA EMISSÃO",
            "NÚMERO",
            "FORNECEDOR",
            "CNPJ/CPF",
            "OBRA",
            "VALOR BRUTO",
            "VALOR LÍQUIDO",
            "ISS",
            "ISS RETIDO?",
            "INSS",
            "IRRF",
            "PIS",
            "COFINS",
            "CSLL",
            "TOTAL RETIDO",
            "DETALHAMENTO",
        ];
        const colCount = cabecalhos.length;

        const wsData = [];
        const merges = [];

        const linhaTitulo = Array(colCount).fill("");
        linhaTitulo[0] = "RELATÓRIO DE DEDUÇÃO DE IMPOSTOS";
        wsData.push(linhaTitulo);
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

        const linhaPeriodo = Array(colCount).fill("");
        linhaPeriodo[0] = `PERÍODO: ${periodoDescricao()}`;
        wsData.push(linhaPeriodo);
        merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });

        const headerRowIndex = wsData.length;
        wsData.push(cabecalhos);

        const dataStartRow = wsData.length;
        recordsExibidos.forEach((r) => {
            wsData.push([
                r.tipo,
                r.dataEmissao ? formatDateWithoutHour(r.dataEmissao) : "",
                r.numero,
                (r.fornecedor || "").toUpperCase(),
                r.cnpjFornecedor || "",
                r.obra || "",
                Number(r.valorBruto) || 0,
                Number(r.valorLiquido) || 0,
                Number(r.valorIss) || 0,
                r.valorIss > 0 ? (r.issRetido ? "SIM" : "NÃO") : "",
                Number(r.valorInss) || 0,
                Number(r.valorIr) || 0,
                Number(r.valorPis) || 0,
                Number(r.valorCofins) || 0,
                Number(r.valorCsll) || 0,
                Number(r.valorTotalRetencoes) || Number(r.diferenca) || 0,
                r.origemDiferenca || "",
            ]);
        });
        const dataEndRow = wsData.length - 1;

        const totalRowIndex = wsData.length;
        const linhaTotal = Array(colCount).fill("");
        linhaTotal[0] = "TOTAL";
        linhaTotal[6] = Number(totais.valorBruto) || 0;
        linhaTotal[7] = Number(totais.valorLiquido) || 0;
        linhaTotal[8] = Number(totais.totalIss) || 0;
        linhaTotal[10] = Number(totais.totalInss) || 0;
        linhaTotal[11] = Number(totais.totalIr) || 0;
        linhaTotal[15] = Number(totais.totalRetencoes) || 0;
        wsData.push(linhaTotal);
        merges.push({ s: { r: totalRowIndex, c: 0 }, e: { r: totalRowIndex, c: 5 } });

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        worksheet["!merges"] = merges;

        const aplicarEstilo = (r, c, estilo, numerico = false) => {
            const ref = XLSX.utils.encode_cell({ r, c });
            if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
            worksheet[ref].s = estilo;
            if (numerico && worksheet[ref].v !== "") worksheet[ref].t = "n";
        };

        for (let c = 0; c < colCount; c++) aplicarEstilo(0, c, tituloStyle);
        for (let c = 0; c < colCount; c++) aplicarEstilo(1, c, subtituloStyle);
        for (let c = 0; c < colCount; c++) aplicarEstilo(headerRowIndex, c, headerStyle);

        for (let r = dataStartRow; r <= dataEndRow; r++) {
            for (let c = 0; c < colCount; c++) {
                if ((c >= 6 && c <= 8) || (c >= 10 && c <= 15)) aplicarEstilo(r, c, cellCurrencyStyle, true);
                else aplicarEstilo(r, c, cellStyle);
            }
        }

        for (let c = 0; c < colCount; c++) {
            const ehValor = (c >= 6 && c <= 8) || (c >= 10 && c <= 15);
            aplicarEstilo(totalRowIndex, c, ehValor ? totalStyle : totalLabelStyle, ehValor);
        }

        worksheet["!cols"] = [
            { wch: 12 }, // TIPO
            { wch: 14 }, // DATA
            { wch: 14 }, // NÚMERO
            { wch: 40 }, // FORNECEDOR
            { wch: 20 }, // CNPJ
            { wch: 34 }, // OBRA
            { wch: 16 }, // BRUTO
            { wch: 16 }, // LÍQUIDO
            { wch: 14 }, // ISS
            { wch: 12 }, // ISS RETIDO?
            { wch: 14 }, // INSS
            { wch: 14 }, // IRRF
            { wch: 12 }, // PIS
            { wch: 12 }, // COFINS
            { wch: 12 }, // CSLL
            { wch: 16 }, // TOTAL RETIDO
            { wch: 60 }, // DETALHAMENTO
        ];
        worksheet["!rows"] = [{ hpt: 26 }, { hpt: 20 }, { hpt: 32 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Dedução de Impostos");
        XLSX.writeFile(workbook, "Relatorio_Deducao_Impostos.xlsx");
    };

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div>
                    <h1 className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Dedução de Impostos</h1>
                    <p className="text-sm text-stone-600 mt-1">
                        Notas de serviço do período com imposto destacado ou retido — ISS, INSS, IRRF, PIS/COFINS/CSLL —
                        para repassar à contabilidade. O ISS destacado e <b>não</b> retido também aparece: quando o prestador
                        é de fora do município, o recolhimento costuma recair sobre o tomador.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="dataInicio" className="text-slate-700 text-sm font-medium">Data Inicial (Emissão)</label>
                        <input
                            id="dataInicio"
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataInicio}
                            onChange={(e) => setFilters({ ...filters, dataInicio: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="dataFim" className="text-slate-700 text-sm font-medium">Data Final (Emissão)</label>
                        <input
                            id="dataFim"
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataFim}
                            onChange={(e) => setFilters({ ...filters, dataFim: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="tipo" className="text-slate-700 text-sm font-medium">Tipo de Nota</label>
                        <select
                            id="tipo"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.tipo}
                            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                        >
                            <option value="servico">Serviço</option>
                            <option value="material">Material</option>
                            <option value="todos">Todos</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="criterio" className="text-slate-700 text-sm font-medium">Critério</label>
                        <select
                            id="criterio"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.criterio}
                            onChange={(e) => setFilters({ ...filters, criterio: e.target.value })}
                        >
                            <option value="imposto">Com imposto (retido ou destacado)</option>
                            <option value="retencao">Somente com imposto retido</option>
                            <option value="diferenca">Somente bruto ≠ líquido</option>
                            <option value="sem-dados">Sem dados de imposto (sem XML)</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="fornecedorId" className="text-slate-700 text-sm font-medium">Fornecedor</label>
                        <select
                            id="fornecedorId"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 disabled:bg-slate-100"
                            value={refino.fornecedor}
                            onChange={(e) => setRefino({ ...refino, fornecedor: e.target.value })}
                            disabled={opcoesFornecedor.length === 0}
                            title={opcoesFornecedor.length === 0 ? "Disponível após carregar o relatório" : ""}
                        >
                            <option value="">Todos</option>
                            {opcoesFornecedor.map((nome) => (
                                <option key={nome} value={nome}>{nome.toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="obraId" className="text-slate-700 text-sm font-medium">Obra</label>
                        <select
                            id="obraId"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 disabled:bg-slate-100"
                            value={refino.obra}
                            onChange={(e) => setRefino({ ...refino, obra: e.target.value })}
                            disabled={opcoesObra.length === 0}
                            title={opcoesObra.length === 0 ? "Disponível após carregar o relatório" : ""}
                        >
                            <option value="">Todas</option>
                            {opcoesObra.map((nome) => (
                                <option key={nome} value={nome}>{nome.toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <p className="text-xs text-slate-500 -mt-3">
                    Período e tipo são aplicados ao clicar em <b>Filtrar</b>. Fornecedor e obra refinam o resultado já
                    carregado, listando apenas o que aparece no relatório.
                </p>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="px-4 py-2 bg-white rounded-md border border-stone-500 disabled:opacity-60"
                        onClick={clearFilters}
                        disabled={isLoading}
                    >
                        <span className="text-stone-500 text-sm">Limpar</span>
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-stone-600 rounded-md disabled:opacity-60"
                        onClick={applyFilters}
                        disabled={isLoading}
                    >
                        <span className="text-white text-sm">{isLoading ? "Filtrando..." : "Filtrar"}</span>
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-60"
                        onClick={exportarExcel}
                        disabled={isLoading || recordsExibidos.length === 0}
                        title={recordsExibidos.length === 0 ? "Filtre os dados para habilitar a exportação" : "Exportar para Excel"}
                    >
                        <img className="w-4 h-4" src="/images/icon/upload.png" alt="" />
                        <span>Exportar Excel</span>
                    </button>
                </div>

                {cobertura.semDadosImposto > 0 && (
                    <div className="bg-red-50 border border-red-300 text-red-900 text-sm rounded-md px-4 py-3">
                        <b>Cobertura parcial:</b> {cobertura.semDadosImposto} de {cobertura.totalNotasServicoPeriodo} nota(s)
                        de serviço do período não têm XML no sistema, então <b>não foi possível apurar o imposto delas</b> —
                        não apareceram aqui e isso <b>não</b> significa que não tenham retenção. Costumam ser notas lançadas
                        manualmente. Selecione o critério <i>“Sem dados de imposto”</i> para listá-las e conferir uma a uma.
                    </div>
                )}

                {totais.quantidadeMaterial > 0 && (
                    <div className="bg-blue-50 border border-blue-300 text-blue-900 text-sm rounded-md px-4 py-3">
                        <b>Atenção:</b> em notas de <b>material</b> a diferença corresponde à composição da NF-e
                        (frete, IPI, ICMS-ST, despesas ou desconto) e <b>não</b> a imposto retido — pode inclusive ser
                        negativa. O imposto retido a recolher aparece apenas nas notas de <b>serviço</b>.
                    </div>
                )}

                {recordsExibidos.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">Notas encontradas</div>
                            <div className="text-lg font-semibold text-slate-800">{recordsExibidos.length}</div>
                            <div className="text-xs text-slate-500 mt-1">
                                {totais.quantidadeServico} serviço · {totais.quantidadeMaterial} material
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">Total bruto</div>
                            <div className="text-lg font-semibold text-slate-800">{formatCurrencyFromDb(totais.valorBruto)}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">ISS destacado</div>
                            <div className="text-lg font-semibold text-slate-800">{formatCurrencyFromDb(totais.totalIss)}</div>
                            <div className="text-xs text-slate-500 mt-1">Líquido: {formatCurrencyFromDb(totais.valorLiquido)}</div>
                        </div>
                        <div className="bg-amber-50 border border-amber-300 rounded-md p-4">
                            <div className="text-xs text-amber-800">Imposto retido a recolher</div>
                            <div className="text-lg font-semibold text-amber-900">{formatCurrencyFromDb(totais.totalRetencoes)}</div>
                            <div className="text-xs text-amber-800 mt-1">
                                {totais.quantidadeComRetencao} nota(s) com retenção
                            </div>
                        </div>
                    </div>
                )}

                {isLoading && (
                    <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
                        <div className="text-stone-600 text-sm">Carregando...</div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Tipo</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Data Emissão</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Número</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Fornecedor</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">Obra</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Valor Bruto</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Valor Líquido</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">ISS</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">INSS</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">IRRF</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Total Retido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && recordsExibidos.length > 0 ? (
                                recordsExibidos.map((r) => (
                                    <tr key={`${r.tipo}-${r.notaId}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.tipo}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.dataEmissao ? formatDateWithoutHour(r.dataEmissao) : ""}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.numero}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm" title={r.cnpjFornecedor}>{(r.fornecedor || "").toUpperCase()}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">{r.obra || "-"}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{formatCurrencyFromDb(r.valorBruto)}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{formatCurrencyFromDb(r.valorLiquido)}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">
                                            {r.valorIss > 0 ? (
                                                <span title={r.issRetido ? "ISS retido pelo tomador" : "ISS destacado, NÃO retido"}>
                                                    {formatCurrencyFromDb(r.valorIss)}
                                                    <span className={r.issRetido ? "ml-1 text-amber-700 font-medium" : "ml-1 text-slate-400"}>
                                                        {r.issRetido ? "(retido)" : "(não retido)"}
                                                    </span>
                                                </span>
                                            ) : "-"}
                                        </td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{r.valorInss > 0 ? formatCurrencyFromDb(r.valorInss) : "-"}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{r.valorIr > 0 ? formatCurrencyFromDb(r.valorIr) : "-"}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-amber-800 text-sm text-right font-medium" title={r.origemDiferenca}>
                                            {r.valorTotalRetencoes > 0 ? formatCurrencyFromDb(r.valorTotalRetencoes) : formatCurrencyFromDb(r.diferenca)}
                                        </td>
                                    </tr>
                                ))
                            ) : !isLoading ? (
                                <tr>
                                    <td colSpan={11} className="text-center py-10 text-slate-500">
                                        {!hasSearched
                                            ? "Defina os filtros e clique em Filtrar para carregar os dados."
                                            : records.length > 0
                                                ? "Nenhuma nota corresponde ao fornecedor/obra selecionados."
                                                : "Nenhuma nota com diferença entre valor bruto e líquido no período."}
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                        {recordsExibidos.length > 0 && (
                            <tfoot>
                                <tr className="bg-stone-100 font-semibold">
                                    <td className="px-4 py-2 border border-slate-200 text-right" colSpan={5}>Total</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totais.valorBruto)}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totais.valorLiquido)}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totais.totalIss)}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totais.totalInss)}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(totais.totalIr)}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right text-amber-800">{formatCurrencyFromDb(totais.totalRetencoes)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                <AlertDialog isVisible={showAlertDialog} message={message} onClose={() => setShowAlertDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioDeducaoImpostos;
